// src/helpers/dragHelpers.js
import { docMapKey, folderMapKey, findItem } from './mapKey';

const makeCollisionSafeTabId = (baseId, existingTabs = []) => {
  const normalizedBase = String(baseId || 'tab-doc');
  const existingIds = new Set((Array.isArray(existingTabs) ? existingTabs : []).map(t => String(t?.id || '')));
  if (!existingIds.has(normalizedBase)) return normalizedBase;

  let suffix = 2;
  let candidate = `${normalizedBase}-${suffix}`;
  while (existingIds.has(candidate)) {
    suffix += 1;
    candidate = `${normalizedBase}-${suffix}`;
  }
  return candidate;
};

const parseTypedDragId = (value) => {
  if (typeof value !== 'string') return null;
  const separatorIndex = value.indexOf(':');
  if (separatorIndex <= 0) return null;

  return {
    kind: value.slice(0, separatorIndex),
    id: value.slice(separatorIndex + 1)
  };
};

const buildProjectDocHref = (docId) => {
  const normalizedId = String(docId || '').trim();
  if (!normalizedId) return '';
  return `ghost://doc/${normalizedId}`;
};

export const dragStart = (e, itemId, setDraggedId, kind = 'doc', metadata = null) => {
  try {
    const meta = metadata && typeof metadata === 'object' ? metadata : {};
    const payload = JSON.stringify({ id: String(itemId), kind, ...meta });
    e?.dataTransfer?.setData?.('application/ghost', payload);
    // keep a plain text fallback for older consumers
    e?.dataTransfer?.setData?.('text/plain', String(itemId));
  } catch (err) {
    // best-effort
    e?.dataTransfer?.setData?.('text/plain', String(itemId));
  }

  setDraggedId(`${kind}:${itemId}`);
};

let _hoverTimer = null;
let _hoverTargetId = null;

export const dragOver = (e, item, setDragOverId, expandFolder) => {
  e?.preventDefault?.();
  const id = item?.id ?? null;
  const typedId = item?.type ? `${item.type}:${id}` : id;
  setDragOverId(typedId);

  // Only expand folders after hovering for 1 second to avoid accidental opens
  if (item?.type === 'folder' && typeof expandFolder === 'function') {
    if (_hoverTargetId !== id) {
      if (_hoverTimer) {
        clearTimeout(_hoverTimer);
        _hoverTimer = null;
      }
      _hoverTargetId = id;
      _hoverTimer = setTimeout(() => {
        try {
          expandFolder(id);
        } catch (err) {
          // swallow
        }
        _hoverTimer = null;
      }, 1000);
    }
  } else {
    if (_hoverTimer) {
      clearTimeout(_hoverTimer);
      _hoverTimer = null;
      _hoverTargetId = null;
    }
  }
};

export const dragEnd = (setDraggedId, setDragOverId) => {
  setDraggedId(null);
  setDragOverId(null);
  if (_hoverTimer) {
    clearTimeout(_hoverTimer);
    _hoverTimer = null;
    _hoverTargetId = null;
  }
};

// ---------------- Drop Helpers ----------------
export const dropOnItem = async (
  e,
  targetItem,
  draggedId,
  itemsMap,
  setItems,
  moveItem,
  handleDragEnd,
  updateTreeArray
) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();

  const parsedDraggedId = parseTypedDragId(draggedId);
  const dragged = parsedDraggedId
    ? findItem(itemsMap, parsedDraggedId.id, parsedDraggedId.kind)
    : findItem(itemsMap, draggedId);
  if (!dragged || !targetItem || dragged.id === targetItem.id) return handleDragEnd();

  const newParentId = targetItem.type === 'folder' ? targetItem.id : targetItem.parent_id;

  try {
    await moveItem(dragged.id, newParentId, dragged.type, itemsMap, setItems);
  } catch (err) {
    console.error('[dropOnItem] moveItem error', err);
  }

  handleDragEnd();
};


export const dropInProject = async (e, draggedId, itemsMap, setItems, moveItem, handleDragEnd) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();

  const parsedDraggedId = parseTypedDragId(draggedId);
  const dragged = parsedDraggedId
    ? findItem(itemsMap, parsedDraggedId.id, parsedDraggedId.kind)
    : findItem(itemsMap, draggedId);
  if (!dragged) return handleDragEnd();

  try {
    await moveItem(dragged.id, null, dragged.type, itemsMap, setItems);
  } catch (err) {
    console.error('[dropInProject] moveItem error', err);
  }

  handleDragEnd();
};


export const dropInTabs = (
  e,
  draggedId,
  itemsMap,
  tabs,
  setTabs,
  setActiveTabId,
  dropIndex = null,
  pane = 'left'
) => {
  e.preventDefault();
  e.stopPropagation();

  // Try to parse richer payload from dataTransfer
  let payload = null;
  let rawPayload = '';
  try {
    rawPayload = e?.dataTransfer?.getData?.('application/ghost') || '';
    if (rawPayload) payload = JSON.parse(rawPayload);
  } catch (err) {
    payload = null;
  }

  const parsedDraggedId = parseTypedDragId(draggedId);
  const kind = payload?.kind || parsedDraggedId?.kind || 'doc';
  const id = payload?.id || parsedDraggedId?.id || String(draggedId);

  const convertToGlobalIndex = (paneRelativeIndex, sourceTabs) => {
    if (paneRelativeIndex === null) return sourceTabs.length;

    const paneTabs = sourceTabs.filter(t => (t.pane || 'left') === pane);
    if (paneRelativeIndex >= paneTabs.length) {
      const lastPaneTab = paneTabs[paneTabs.length - 1];
      return lastPaneTab ? sourceTabs.findIndex(t => t.id === lastPaneTab.id) + 1 : 0;
    }

    const targetTab = paneTabs[paneRelativeIndex];
    return targetTab ? sourceTabs.findIndex(t => t.id === targetTab.id) : 0;
  };

  const getItemFromMap = (map, key, itemKind = 'doc') => {
    if (!map || !map.get) return null;
    const typedKey = itemKind === 'folder' ? folderMapKey(key) : docMapKey(key);
    return map.get(typedKey) ?? null;
  };

  const createTabFromDraggedItem = (item, paneName, existingTabs = []) => ({
    id: makeCollisionSafeTabId(`tab-${String(item.id)}`, existingTabs),
    docId: item.id,
    label: item.name || 'Untitled Doc',
    pane: paneName,
    docType: item.docType || null,
    readOnly: Boolean(item.readOnly) || item.docType === 'chat'
  });

  if (isVersionDragEvent(e, payload, rawPayload)) {
    let versionPayload = null;
    try {
      const rawVersion = e?.dataTransfer?.getData?.('application/x-ghost-version') || '';
      if (rawVersion) versionPayload = JSON.parse(rawVersion);
    } catch (err) {
      versionPayload = null;
    }

    const versionId = String(versionPayload?.versionId || payload?.id || '').trim();
    const sourceDocId = String(versionPayload?.docId || '').trim();
    if (!versionId || !sourceDocId) return false;

    const existingVersionTabIndex = tabs.findIndex(t =>
      Boolean(t?.isVersion) &&
      String(t?.versionId || '') === versionId &&
      String(t?.sourceDocId || '') === sourceDocId
    );

    if (existingVersionTabIndex !== -1) {
      const next = [...tabs];
      const [existing] = next.splice(existingVersionTabIndex, 1);
      existing.pane = pane;
      const globalIndex = convertToGlobalIndex(dropIndex, next);
      const insertAt = Math.max(0, Math.min(globalIndex, next.length));
      next.splice(insertAt, 0, existing);
      setTabs(next);
      setActiveTabId(existing.id);
      return true;
    }

    const versionDocId = `version-${sourceDocId}-${versionId.replace(/[^a-zA-Z0-9-_]/g, '-')}`;
    const sourceLabel = String(versionPayload?.label || 'Document').trim() || 'Document';
    const newVersionTab = {
      id: `version-tab-${sourceDocId}-${versionId}`,
      docId: versionDocId,
      label: `${sourceLabel} · ${versionId}`,
      readOnly: true,
      toolbarVariant: 'version',
      pane,
      isVersion: true,
      versionId,
      versionSavedAt: null,
      versionMetadata: null,
      sourceDocId
    };

    setTabs(prev => {
      const next = [...prev];
      const globalIndex = convertToGlobalIndex(dropIndex, next);
      const insertAt = Math.max(0, Math.min(globalIndex, next.length));
      next.splice(insertAt, 0, newVersionTab);
      return next;
    });
    setActiveTabId(newVersionTab.id);
    return true;
  }

  // lightweight: no debug logging here

  // Reordering an existing tab
  if (kind === 'tab') {
    const from = tabs.findIndex(t => t.id === id);
    // reordering tab
    if (from === -1) return;

    // Convert pane-relative dropIndex to global index
    let to;
    if (dropIndex === null) {
      to = tabs.length;
    } else {
      // Find all tabs in the target pane up to dropIndex
      const paneTabs = tabs.filter(t => (t.pane || 'left') === pane);
      if (dropIndex >= paneTabs.length) {
        // Appending at end of pane - find last tab of this pane in global array
        const lastPaneTab = paneTabs[paneTabs.length - 1];
        to = lastPaneTab ? tabs.findIndex(t => t.id === lastPaneTab.id) + 1 : 0;
      } else {
        // Inserting before a specific tab in the pane
        const targetTab = paneTabs[dropIndex];
        to = targetTab ? tabs.findIndex(t => t.id === targetTab.id) : 0;
      }
    }

    const next = [...tabs];
    const [moved] = next.splice(from, 1);
    // Update the pane when moving
    moved.pane = pane;
    // Adjust insertion index if we removed a tab before it
    const adjustedTo = from < to ? to - 1 : to;
    next.splice(adjustedTo, 0, moved);

    setTabs(next);
    setActiveTabId(prev => (prev === id ? id : prev));
    return true;
  }

  // Dropping a doc into the tab strip (open or move)
  const fallbackDraggedDoc = {
    id,
    type: 'doc',
    name: String(payload?.name || payload?.label || '').trim() || 'Untitled Doc',
    docType: String(payload?.docType || '').trim().toLowerCase() === 'chat' ? 'chat' : null,
    readOnly: String(payload?.docType || '').trim().toLowerCase() === 'chat' || Boolean(payload?.readOnly)
  };
  const draggedItem = getItemFromMap(itemsMap, id, kind) || fallbackDraggedDoc;
  // dropping a doc into tabs
  if (!draggedItem || draggedItem.type !== 'doc') return false;

  // If tab for doc already exists, move it to target index and activate
  const draggedDocId = String(draggedItem.id);
  const existingTabIndex = tabs.findIndex(t => String(t.docId) === draggedDocId);
  if (existingTabIndex !== -1) {
    // move existing tab
    const next = [...tabs];
    const [existing] = next.splice(existingTabIndex, 1);
    existing.pane = pane; // Update pane when moving
    const globalIndex = convertToGlobalIndex(dropIndex, next);
    // Adjust for removal if we removed before insertion point
    const insertAt = Math.max(0, Math.min(globalIndex, next.length));
    next.splice(insertAt, 0, existing);
    setTabs(next);
    setActiveTabId(existing.id);
    return true;
  }

  // Create new tab at index
  let newTabId = null;
  setTabs(prev => {
    const next = [...prev];
    const newTab = createTabFromDraggedItem(draggedItem, pane, next);
    newTabId = newTab.id;
    const globalIndex = convertToGlobalIndex(dropIndex, next);
    const insertAt = Math.max(0, Math.min(globalIndex, next.length));
    // create new tab
    next.splice(insertAt, 0, newTab);
    return next;
  });

  setActiveTabId(newTabId || `tab-${String(draggedItem.id)}`);
  return true;
};

const VERSION_ID_REGEX = /^\d{4}-\d{2}-\d{2}_\d{2}-\d{2}(?:-\d{2})?$/;

function parseDragPayload(event) {
  let payload = null;
  let rawPayload = '';
  try {
    rawPayload = event?.dataTransfer?.getData?.('application/ghost') || '';
    if (rawPayload) payload = JSON.parse(rawPayload);
  } catch (err) {
    payload = null;
  }

  return { payload, rawPayload };
}

function isVersionDragEvent(event, payload = null, rawPayload = '') {
  const transferTypes = Array.from(event?.dataTransfer?.types || []).map(type => String(type || '').toLowerCase());
  const hasVersionMimeType = transferTypes.includes('application/x-ghost-version');
  const versionPayload = event?.dataTransfer?.getData?.('application/x-ghost-version');
  const plainText = event?.dataTransfer?.getData?.('text/plain') || '';
  const isPlainVersionId = VERSION_ID_REGEX.test(String(plainText || '').trim());
  const rawPayloadMarksVersion = typeof rawPayload === 'string' && /"kind"\s*:\s*"version"/i.test(rawPayload);
  const kind = payload?.kind || null;

  return kind === 'version' || rawPayloadMarksVersion || hasVersionMimeType || Boolean(versionPayload) || isPlainVersionId;
}

function resolveInlineSelectionPos(editor, preferredPos) {
  const doc = editor?.state?.doc;
  if (!doc || typeof doc.resolve !== 'function') return null;
  const docSize = Number(doc?.content?.size);
  if (!Number.isFinite(docSize)) return null;

  const clamped = Math.max(0, Math.min(Number(preferredPos || 0), docSize));
  const tryResolve = (candidate) => {
    try {
      const $pos = doc.resolve(candidate);
      if ($pos?.parent?.inlineContent) return candidate;
    } catch (err) {
      return null;
    }
    return null;
  };

  const direct = tryResolve(clamped);
  if (direct !== null) return direct;

  // Scan nearby positions to avoid TextSelection errors on block/doc boundaries.
  for (let delta = 1; delta <= 40; delta += 1) {
    const left = clamped - delta;
    if (left >= 0) {
      const resolvedLeft = tryResolve(left);
      if (resolvedLeft !== null) return resolvedLeft;
    }

    const right = clamped + delta;
    if (right <= docSize) {
      const resolvedRight = tryResolve(right);
      if (resolvedRight !== null) return resolvedRight;
    }
  }

  return null;
}

export const dropInEditor = (e, draggedId, itemsMap, tabs, setTabs, setActiveTabId, mode = 'replace', pane = 'left') => {
  e.preventDefault();
  e.stopPropagation();

  const { payload, rawPayload } = parseDragPayload(e);
  if (isVersionDragEvent(e, payload, rawPayload)) return;

  const parsedDraggedId = parseTypedDragId(draggedId);
  const resolvedKind = payload?.kind || parsedDraggedId?.kind || 'doc';
  const id = payload?.id || parsedDraggedId?.id || String(draggedId);

  if (resolvedKind === 'tab') {
    const existingIndex = tabs.findIndex(t => t.id === id);
    if (existingIndex === -1) return;

    const existingTab = tabs[existingIndex];
    const fromPane = existingTab.pane || 'left';
    if (fromPane === pane) {
      setActiveTabId(existingTab.id);
      return;
    }

    const next = [...tabs];
    const [moved] = next.splice(existingIndex, 1);
    moved.pane = pane;

    let insertAt = next.length;
    for (let i = next.length - 1; i >= 0; i--) {
      if ((next[i].pane || 'left') === pane) {
        insertAt = i + 1;
        break;
      }
    }

    next.splice(insertAt, 0, moved);
    setTabs(next);
    setActiveTabId(moved.id);
    return;
  }

  const getItemFromMap = (map, key, itemKind = 'doc') => {
    if (!map || !map.get) return null;
    const typedKey = itemKind === 'folder' ? folderMapKey(key) : docMapKey(key);
    return map.get(typedKey) ?? null;
  };

  const createTabFromDraggedItem = (item, paneName, existingTabs = []) => ({
    id: makeCollisionSafeTabId(`tab-${String(item.id)}`, existingTabs),
    docId: item.id,
    label: item.name || 'Untitled Doc',
    pane: paneName,
    docType: item.docType || null,
    readOnly: Boolean(item.readOnly) || item.docType === 'chat'
  });

  const draggedItem = getItemFromMap(itemsMap, id, resolvedKind);
  if (!draggedItem || draggedItem.type !== 'doc') return;

  const draggedDocId = String(draggedItem.id);
  const existingTab = tabs.find(t => String(t.docId) === draggedDocId);
  if (existingTab && mode === 'replace') {
    setActiveTabId(existingTab.id);
    return;
  }

  let newTabId = null;
  setTabs(prev => {
    const newTab = createTabFromDraggedItem(draggedItem, pane, prev);
    newTabId = newTab.id;
    return [...prev, newTab];
  });
  setActiveTabId(newTabId || `tab-${String(draggedItem.id)}`);
};

// ---------------- Dropcursor helpers moved from EditorPanel ----------------
const DROP_INDICATOR_SELECTOR = '.prosemirror-dropcursor-block, .prosemirror-dropcursor-inline, .insertLine';
const HIDE_ON_SELECTION_CLASS = 'ghost-hide-dropcursor-on-selection';

function hasActiveEditorSelection(ev) {
  try {
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount < 1 || selection.isCollapsed) return false;

    const range = selection.getRangeAt(0);
    const commonNode = range.commonAncestorContainer;
    const commonEl = commonNode?.nodeType === 1 ? commonNode : commonNode?.parentElement;
    if (!commonEl || !commonEl.closest) return false;

    const editorRoot = commonEl.closest('.ProseMirror');
    if (!editorRoot) return false;

    const targetNode = ev?.target;
    const targetEl = targetNode?.nodeType === 1 ? targetNode : targetNode?.parentElement;
    if (!targetEl || !targetEl.closest) return true;

    const targetEditorRoot = targetEl.closest('.ProseMirror');
    return targetEditorRoot === editorRoot;
  } catch (err) {
    return false;
  }
}

function hideDropIndicators() {
  try {
    const nodes = document.querySelectorAll(DROP_INDICATOR_SELECTOR);
    nodes.forEach(n => { try { n.setAttribute('data-ghost-hidden', 'true'); n.classList.add('ghost-hidden'); } catch (e) {} });
    try { observeAndHideDropcursor(800); } catch (e) {}
  } catch (err) {}
}

export function observeAndHideDropcursor(duration = 500) {
  try {
    const root = document.body;
    const hideIfMatch = (node) => {
      try {
        if (!node) return;
        if (node.nodeType !== 1) return;
        const matches = [];
        if (node.classList && (node.classList.contains('prosemirror-dropcursor-block') || node.classList.contains('prosemirror-dropcursor-inline') || node.classList.contains('insertLine'))) matches.push(node);
        if (node.querySelectorAll) {
          const found = node.querySelectorAll(DROP_INDICATOR_SELECTOR);
          if (found && found.length) found.forEach(n => matches.push(n));
        }
        if (matches.length) {
          matches.forEach(n => {
            try { n.setAttribute('data-ghost-hidden', 'true'); n.classList.add('ghost-hidden'); } catch (e) {}
          });
        }
      } catch (e) {}
    };

    try {
      const initial = document.querySelectorAll(DROP_INDICATOR_SELECTOR);
        if (initial && initial.length) {
        initial.forEach(n => { try { n.setAttribute('data-ghost-hidden', 'true'); n.classList.add('ghost-hidden'); } catch (e) {} });
      }
    } catch (e) {}

    const mo = new MutationObserver(mutations => {
      for (const mut of mutations) {
        (mut.addedNodes || []).forEach(n => hideIfMatch(n));
      }
    });
    mo.observe(root, { childList: true, subtree: true });
    setTimeout(() => { try { mo.disconnect(); } catch (e) {} }, duration);
    return mo;
  } catch (err) {
    return null;
  }
}

export function clearInsertIndicators() {
  try { document.body.classList.remove(HIDE_ON_SELECTION_CLASS); } catch (err) {}
  hideDropIndicators();
}

export function restoreHiddenIndicators(ev) {
  try {
    const isSelectionHoverEvent = (ev?.type === 'dragover' || ev?.type === 'dragenter') && hasActiveEditorSelection(ev);
    if (isSelectionHoverEvent) {
      try { document.body.classList.add(HIDE_ON_SELECTION_CLASS); } catch (err) {}
      hideDropIndicators();
      return;
    }

    try { document.body.classList.remove(HIDE_ON_SELECTION_CLASS); } catch (err) {}

    // Restore hidden indicators regardless of the event target; callers
    // already filter when needed (e.g., only listening on dragover)
    const nodes = document.querySelectorAll('.ghost-hidden');
    nodes.forEach(n => {
      try {
        n.classList.remove('ghost-hidden');
        n.removeAttribute('data-ghost-hidden');
        try { n.style.removeProperty('display'); } catch (e) {}
        try { n.style.removeProperty('visibility'); } catch (e) {}
        try { n.style.removeProperty('height'); } catch (e) {}
      } catch (e) {}
    });
  } catch (err) {}
}

// ---------------- Editor drop handler ----------------
export async function handleEditorDrop(e, context) {
  // context: { tabs, activeTabId, editorInstances, draggedId, loadDocContent, removeChunkAttributes, dropInEditor }
  try {
    const { payload, rawPayload } = parseDragPayload(e);
    const kind = payload?.kind || null;
    const isVersionDrag = isVersionDragEvent(e, payload, rawPayload);

    e.preventDefault();

    if (isVersionDrag) {
      e.stopPropagation();
      try {
        if (typeof context?.onVersionDrop === 'function') {
          await context.onVersionDrop(e);
        }
      } catch (err) {}
      return null;
    }

    if (kind === 'tab') {
      return null;
    }

    e.stopPropagation();

    try { document.body.classList.add('ghost-hide-dropcursor'); } catch (err) {}

    const {
      tabs,
      activeTabId,
      editorInstances,
      draggedId,
      dropInEditor,
      pane = 'left'
    } = context;

    const activeTab = tabs.find(t => t.id === activeTabId);
    const activeDocId = activeTab?.docId;
    const editor = activeDocId ? editorInstances.current[activeDocId] : null;
    const isActiveEditable = Boolean(activeTab?.docId) && !Boolean(activeTab?.readOnly);

    if (!isActiveEditable || !editor || !editor.view) {
      return dropInEditor?.(e, 'new', pane);
    }

    // Only accept explicit app drag payloads or typed draggedId from our tree drag.
    // Never infer doc ids from plain text, otherwise dragging table text like "1" can load doc 1.
    const parsedDraggedId = parseTypedDragId(draggedId);
    let id = null;
    if (payload && payload.kind === 'doc' && payload.id != null) {
      id = String(payload.id);
    } else if (parsedDraggedId && parsedDraggedId.kind === 'doc' && parsedDraggedId.id != null) {
      id = String(parsedDraggedId.id);
    }

    // clear any dataTransfer payloads now that we've read them (best-effort)
    try { if (e.dataTransfer && e.dataTransfer.clearData) e.dataTransfer.clearData(); } catch (err) {}
    if (!id) return null;

    const droppedDocName = String(
      payload?.name || payload?.label || payload?.title || payload?.docName || ''
    ).trim() || `Document ${id}`;
    const linkHref = buildProjectDocHref(id);
    if (!linkHref) return null;

    let insertPos = null;
    try {
      const selection = editor.state?.selection;
      const hasSelectedRange = Boolean(
        selection &&
        Number.isFinite(selection.from) &&
        Number.isFinite(selection.to) &&
        selection.from !== selection.to
      );

      if (hasSelectedRange) {
        editor
          .chain()
          .focus()
          .setTextSelection({ from: selection.from, to: selection.to })
          .setLink({ href: linkHref, target: null, rel: null })
          .run();
        return true;
      }

      const hasPointerCoords = Number.isFinite(e?.clientX) && Number.isFinite(e?.clientY);
      const coords = hasPointerCoords
        ? editor.view.posAtCoords({ left: e.clientX, top: e.clientY })
        : null;
      insertPos = coords?.pos ?? editor.state?.selection?.to ?? 0;

      const toInsert = {
        type: 'text',
        text: droppedDocName,
        marks: [{ type: 'link', attrs: { href: linkHref, target: null, rel: null } }]
      };

      let inserted = false;
      try {
        if (editor.chain) {
          editor.chain().focus().insertContentAt(insertPos, toInsert).run();
          inserted = true;
        } else if (editor.commands && editor.view) {
          editor.commands.insertContentAt(insertPos, toInsert);
          inserted = true;
        }
      } catch (err) {
        try { editor.chain().focus().insertContent(toInsert).run(); inserted = true; } catch (e) { inserted = false; }
      }

      if (inserted) return true;
    } catch (err) {
      return dropInEditor?.(e, 'new', pane);
    } finally {
      try { document.body.classList.remove('ghost-hide-dropcursor'); } catch (e) {}
      try { document.body.classList.remove(HIDE_ON_SELECTION_CLASS); } catch (e) {}
      try {
        if (editor && insertPos != null) {
          const docSize = editor.state && editor.state.doc ? editor.state.doc.content.size : null;
          const safePos = (typeof insertPos === 'number' && docSize != null) ? Math.max(0, Math.min(insertPos, docSize)) : insertPos;
          const inlinePos = resolveInlineSelectionPos(editor, safePos);
          if (inlinePos !== null) {
            if (editor.chain) {
              editor.chain().focus().setTextSelection(inlinePos).run();
            } else if (editor.commands && editor.commands.setTextSelection) {
              try { editor.commands.focus(); } catch (e) {}
              editor.commands.setTextSelection(inlinePos);
            }
          }
        }
      } catch (err) {}

      try {
        const rem = document.querySelectorAll(DROP_INDICATOR_SELECTOR);
        rem.forEach(n => { try { n.setAttribute('data-ghost-hidden', 'true'); n.classList.add('ghost-hidden'); } catch (e) {} });
      } catch (e) {}
    }
  } catch (err) {
    return null;
  }
}
